import { put, select, takeEvery, call } from 'redux-saga/effects';
import MixPanel from 'mixpanel-browser';
import { LoginActions } from './actions';
import { LoginSelectors } from './selectors';
import { TWOFA_EVENT_PREFIX } from './constants';
import ApiService from '../../../Services/ApiService';
import { displayNotification } from '../../../Utils/GenericUtils';

function track(event, props) {
  try {
    MixPanel.track(`${TWOFA_EVENT_PREFIX}${event}`, props);
  } catch (e) {
    // analytics must never break the auth flow
  }
}

export function* submitLogin(action) {
  try {
    const { email, password, remember, captcha, deviceToken, next } = action.payload;
    const res = yield call(ApiService.login, { email, password, remember, captcha, deviceToken, next });
    const data = res.data || res;
    if (data.requires_captcha) {
      yield put(LoginActions.setRequiresCaptcha(true));
      yield put(LoginActions.submitLoginFailed({}));
      return;
    }
    if (data.requires_2fa) {
      // Carry the submitted email into the slice so the 2FA events can attribute the user.
      yield put(LoginActions.setRequires2fa({ ...data, email }));
      yield put(LoginActions.submitLoginSucceeded());
      return;
    }
    yield put(LoginActions.submitLoginSucceeded());
    window.location = data.redirect_url || '/';
  } catch (e) {
    const data = e?.response?.data || {};
    yield put(LoginActions.submitLoginFailed(data));
    if (data.error === 'sso_required') {
      displayNotification('error', 'Error', "Please log in with your organization's single sign-on.");
    }
  }
}

// Wrong code / expired / max-attempts all end the challenge and send the user back to the
// password form; only a plain wrong-code with attempts left keeps the panel open.
function* fail2faBackToPassword(message) {
  displayNotification('error', 'Error', message);
  yield put(LoginActions.reset2fa());
}

export function* verify2fa(action) {
  const { challengeId, code } = action.payload;
  const userEmail = yield select(LoginSelectors.loginTwoFaEmailSelector);
  try {
    const res = yield call(ApiService.verify2fa, { challengeId, code });
    const data = res.data || res;
    track('2FA Verified', { userEmail });
    // Success navigates away — the full page reload discards the verifying spinner state.
    window.location = data.redirect_url || '/';
  } catch (e) {
    const status = e?.response?.status;
    const data = e?.response?.data || {};
    if (status === 410 || data.error === 'challenge_expired') {
      track('2FA Challenge Expired', { userEmail });
      yield* fail2faBackToPassword('Code expired. Please sign in again.');
      return;
    }
    if (data.error === 'max_attempts_exceeded') {
      track('2FA Max Attempts', { userEmail });
      yield* fail2faBackToPassword('Too many wrong codes. Please sign in again.');
      return;
    }
    track('2FA Failed', { userEmail, attempts_remaining: data.attempts_remaining });
    yield put(LoginActions.verify2faFailed(data));
  }
}

export function* resend2fa(action) {
  const { challengeId } = action.payload;
  const userEmail = yield select(LoginSelectors.loginTwoFaEmailSelector);
  try {
    const res = yield call(ApiService.resend2fa, { challengeId });
    const data = res.data || res;
    track('2FA Resent', { userEmail });
    yield put(LoginActions.resend2faSucceeded(data));
  } catch (e) {
    const status = e?.response?.status;
    const data = e?.response?.data || {};
    track('2FA Resend Failed', { userEmail, reason: data.error });
    if (data.error === 'max_resends_exceeded') {
      yield* fail2faBackToPassword('Too many code resends. Please sign in again.');
      return;
    }
    if (status === 410 || data.error === 'challenge_expired') {
      yield* fail2faBackToPassword('Code expired. Please sign in again.');
      return;
    }
    if (data.error === 'resend_cooldown') {
      yield put(LoginActions.resend2faFailed(data));
      return;
    }
    displayNotification('error', 'Error', "Couldn't resend code. Please try again.");
    yield put(LoginActions.resend2faFailed(data));
  }
}

export function* watchLogin() {
  yield takeEvery(LoginActions.submitLogin, submitLogin);
  yield takeEvery(LoginActions.verify2fa, verify2fa);
  yield takeEvery(LoginActions.resend2fa, resend2fa);
}
